<script>
import ProcessingEvent from './ProcessingEvent.vue';
import { iIcon } from '@inscribe/signal';

export default {
  name: 'ProcessingEventsAccordion',
  components: {
    ProcessingEvent,
    iIcon,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    processingEvents: {
      type: Array,
      default: () => [],
    },
    allProcessingEventsFinished: {
      type: Boolean,
      default: () => null,
    },
    startExpanded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isExpanded: this.startExpanded,
    };
  },
  computed: {
    accordionStatus() {
      if (
        this.processingEvents.length === 0 ||
        typeof this.allProcessingEventsFinished !== 'boolean'
      ) {
        return '';
      }
      if (this.allProcessingEventsFinished) {
        return 'Complete';
      }
      return 'In progress';
    },
    atLeastOneProcessingEvent() {
      return this.processingEvents.length > 0;
    },
  },
  watch: {
    processingEvents: {
      handler() {
        this.scrollToBottom();
      },
    },
    isExpanded(newValue) {
      if (newValue) {
        this.scrollToBottom();
      }
    },
  },
  methods: {
    toggleAccordion() {
      this.isExpanded = !this.isExpanded;
    },
    scrollToBottom() {
      // Use nextTick to ensure the DOM is updated before scrolling
      this.$nextTick(() => {
        setTimeout(() => {
          const container = this.$el.querySelector(
            '.processing-events-container',
          );
          if (container) {
            container.scrollTo({
              top: container.scrollHeight,
              behavior: 'smooth',
            });
          }
        }, 50);
      });
    },
  },
  mounted() {
    if (this.isExpanded) {
      this.scrollToBottom();
    }
  },
};
</script>

<template>
  <div class="accordion-container i-radius-xl">
    <div
      class="accordion-button i-radius-xl d-flex align-items-center justify-content-between i-pl-7 i-pr-8 i-py-5"
      :class="{ 'is-expanded': isExpanded && atLeastOneProcessingEvent }"
      @click="toggleAccordion"
      data-dd-action-name="document-view-processing-status-toggle-accordion-button"
    >
      <div class="d-flex align-items-center i-gap-5">
        <i-icon
          v-if="atLeastOneProcessingEvent"
          :class="{ 'rotate-180': isExpanded }"
          symbol="medium-chevron-down"
          :size="16"
          class="i-text-neutral-110 chevron-icon"
        />
        <span class="accordion-title i-text-subheading-3 i-text-neutral-120">{{
          title
        }}</span>
      </div>
      <span class="i-text-body-3 i-text-neutral-100">
        {{ accordionStatus }}
      </span>
    </div>

    <transition name="slide-fade">
      <div
        v-show="isExpanded && atLeastOneProcessingEvent"
        class="processing-events-container d-flex flex-column i-px-8 i-py-4"
      >
        <ProcessingEvent
          v-for="({ type, status }, index) in processingEvents"
          :key="index"
          :type="type"
          :status="status"
        />
      </div>
    </transition>
  </div>
</template>

<style scoped lang="scss">
.accordion-container {
  border: 1px solid $light-neutral-60;
}

.accordion-button {
  cursor: pointer;

  &:hover {
    background: $light-neutral-20;
  }
}

.accordion-button.is-expanded {
  border-bottom-left-radius: 0;
  border-bottom-right-radius: 0;
  border-bottom: 1px solid $light-neutral-60;
}

.accordion-title {
  font-weight: 600;
}

.chevron-icon {
  transition: transform 0.15s ease-in-out;
}

.rotate-180 {
  transform: rotate(180deg);
}

.processing-events-container {
  max-height: 200px;
  overflow-x: hidden;
  overflow-y: auto;
}
</style>
